// apps/scheduler-worker/src/pages/TaskDetailPage.tsx
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from 'scheduler-ui';
import { useTask } from '../hooks/useTask';
import { useTasks } from '../hooks/useTasks';
import { useStartTask, useStopTask, useCompleteTask } from '../hooks/useTaskActions';
import { TaskDetailCard } from '../components/TaskDetailCard';
import { TaskTimer } from '../components/TaskTimer';
import { TaskActionButtons } from '../components/TaskActionButtons';
import { TaskDetailSkeleton } from '../components/LoadingSkeleton';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  // Fetch task detail
  const { data: taskData, isLoading, error, refetch } = useTask(taskId);

  // Get active task info for timer
  const { data: myTasksData } = useTasks();
  const activeTask = myTasksData?.activeTask;
  const isThisTaskActive = activeTask?.id === taskId;

  // Mutations
  const startMutation = useStartTask();
  const stopMutation = useStopTask();
  const completeMutation = useCompleteTask();

  const handleStart = () => {
    if (taskId) {
      startMutation.mutate(taskId);
    }
  };

  const handleStop = () => {
    if (taskId) {
      stopMutation.mutate(taskId);
    }
  };

  const handleComplete = () => {
    if (taskId) {
      completeMutation.mutate(taskId, {
        onSuccess: () => {
          // Navigate back to task list after completion
          navigate('/');
        },
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
        <TaskDetailSkeleton />
      </div>
    );
  }

  // Error state
  if (error || !taskData) {
    return (
      <div className="p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to load task
          </h2>
          <p className="text-gray-600 mb-4">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button onClick={() => refetch()} variant="secondary" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const task = taskData.data;

  return (
    <div className="p-4 pb-24">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">Back</span>
      </button>

      {/* Timer (when task is active) */}
      <TaskTimer
        startTime={isThisTaskActive && activeTask ? activeTask.startedAt : null}
        isRunning={isThisTaskActive && task.status === 'in_progress'}
        className="mb-4"
      />

      {/* Task details */}
      <TaskDetailCard task={task} />

      {/* Action buttons (fixed at bottom) */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <TaskActionButtons
          status={task.status}
          onStart={handleStart}
          onStop={handleStop}
          onComplete={handleComplete}
          isStarting={startMutation.isPending}
          isStopping={stopMutation.isPending}
          isCompleting={completeMutation.isPending}
          disabled={!!task.blockedBy}
        />
      </div>
    </div>
  );
}
